import React, { useState } from 'react';
import { Box, Typography, Button, Stack, Tooltip } from '@mui/material';
import { Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  CameraWithPricing,
  useProductPriceFormatters,
  formatDate,
  laterDate,
  lastPriceChangeAt,
  historyPrimaryLine,
  mergePriceHistoryWithRejectedReview,
  ProductPriceHistoryRow,
} from '../../utils/productDetailHelpers';
import { catalogRegularFromPricing, getEffectiveMapForProduct, rebateWindowCaption } from '../../utils/mapPricing';
import { StatusChip } from '../ui';
import { PendingReviewChip } from '../PendingReviewChip';
import { tokens } from '../../theme/tokens';
import { useAppTranslation } from '../../i18n/useAppTranslation';

interface ProductPriceBlockProps {
  product: CameraWithPricing;
  history: ProductPriceHistoryRow[];
  isPlatformAdmin: boolean;
  /** A vendor cannot verify a dealer authorization; the lock reads differently for them */
  isVendor?: boolean;
  onReviewDiscount: (decision: 'approved' | 'rejected') => void;
  reviewPending: boolean;
  /**
   * What the store's mapped Shopify listing charges right now (Shopify Admin
   * API). When set it is the lead price: it is what the store's customers see.
   */
  storefrontPrice?: number | null;
}

export function ProductPriceBlock({
  product,
  history,
  isPlatformAdmin,
  isVendor = false,
  onReviewDiscount,
  reviewPending,
  storefrontPrice,
}: ProductPriceBlockProps) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const {
    saleDisplay,
    formatProductPrice,
    rebateDisplay,
    dealerReimbursement,
    dealerCostDetail,
    formatAmountForProduct,
    formatMoneyForProduct,
  } = useProductPriceFormatters();
  // MAP/history figures in this block use the product's own currency (symbol
  // only — the lead price beside them already carries the USD/CAD label).
  const money = (n: number | null | undefined): string => formatMoneyForProduct(product, n) ?? '—';

  const sale = saleDisplay(product);
  const rebate = rebateDisplay(product);
  const em = getEffectiveMapForProduct(product);
  const rebateActive = em?.windowStatus === 'active' && rebate.instantRebate > 0;
  // An upcoming rebate still shows on the product card (getEffectiveMap /
  // rebateWindowCaption advertise it), so surface it here too — captioned as
  // "starts …" rather than folded into the advertised price, which stays the
  // pre-rebate price until the window opens.
  const rebateUpcoming = em?.windowStatus === 'upcoming' && rebate.instantRebate > 0;
  const hasStrike = sale.isSaleActive && sale.regularPrice > 0 && !!sale.regularLabel;
  const credit = dealerReimbursement(product);
  // The API strips dealer cost and reimbursement until this brand's dealer
  // authorization is verified for the store; say so instead of showing nothing.
  const privateLocked = !isPlatformAdmin && (product.vendorPrivateLocked === true || product.pricingData?.vendorPrivateLocked === true);
  const costDetail = !privateLocked ? dealerCostDetail(product) : null;
  const cost = costDetail?.amount ?? 0;
  const fromPromaster = costDetail?.source === 'promaster';
  const promasterDate = fromPromaster ? formatDate(costDetail?.asOf) : null;
  const navigate = useNavigate();
  const { t } = useAppTranslation();

  // A mapped live listing is the storefront's source of truth; catalog retail
  // (possibly a scrape leftover) is only the fallback for an unmapped product.
  const liveStorefront =
    storefrontPrice != null && Number.isFinite(storefrontPrice) && storefrontPrice > 0 ? storefrontPrice : null;
  const leadPrice = liveStorefront != null
    ? formatAmountForProduct(product, liveStorefront)
    : sale.priceLabel || formatProductPrice(product);
  const showsAfterRebate = rebateActive && liveStorefront == null;
  const leadColor = showsAfterRebate ? tokens.signal.positive.ink : tokens.ink.primary;
  const struck = liveStorefront == null && hasStrike ? sale.regularLabel : null;

  const msrp = product.pricing?.msrp;
  // Same currency formatter as the lead price — a hardcoded "$" disagrees with
  // the rest of the block on a non-USD product.
  // Compared with the catalog list price, so a leftover scrape retail above
  // MAP and MSRP does not make MSRP look like a different price.
  const msrpLabel =
    msrp != null && msrp > 0 && Math.abs(msrp - catalogRegularFromPricing(product.pricing)) >= 0.01
      ? formatAmountForProduct(product, msrp)
      : null;
  const leadCaption = liveStorefront != null
    ? 'Live on Shopify'
    : msrpLabel
      ? rebateActive
        ? `MSRP ${msrpLabel} · advertised price`
        : `Retail price · MSRP ${msrpLabel}`
      : null;

  const vendorUpdatedAt = lastPriceChangeAt(product);
  const lastCheckedAt = laterDate(product.pricing?.priceLastChecked, product.pricing?.priceAsOf, product.lastScrapedAt);

  const rows = mergePriceHistoryWithRejectedReview(history, product.pricing?.suspiciousDiscountReview)
    .filter((row) => historyPrimaryLine(row, (n) => formatMoneyForProduct(product, n)) !== 'Price recorded');
  const shownRows = historyOpen ? rows : rows.slice(0, 3);

  const review = product.pricing?.suspiciousDiscountReview;
  const missingPricePending = product.pricing?.missingAdvertisedPriceReview?.status === 'pending';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.1 }}>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, letterSpacing: '-0.02em', color: leadColor, fontVariantNumeric: 'tabular-nums' }}>
              {leadPrice || '—'}
            </Typography>
            {struck && (
              <Typography sx={{ fontSize: '14px', color: tokens.ink.faint, textDecoration: 'line-through', fontVariantNumeric: 'tabular-nums' }}>
                {struck}
              </Typography>
            )}
            {showsAfterRebate && (
              <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: leadColor }}>after rebate</Typography>
            )}
            <PendingReviewChip pricing={product.pricing} sx={{ alignSelf: 'center' }} />
          </Box>
          {leadCaption && (
            <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{leadCaption}</Typography>
          )}
          {(rebateActive || rebateUpcoming) && rebate.instantRebate > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.25, fontSize: '12.5px' }}>
              {rebateUpcoming && (
                <StatusChip
                  label="Upcoming"
                  tone="caution"
                  sx={{ height: 20, alignSelf: 'center', '& .MuiChip-label': { px: 0.75, fontSize: 11, fontWeight: 600 } }}
                />
              )}
              <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary', fontVariantNumeric: 'tabular-nums', fontSize: 'inherit' }}>
                {formatAmountForProduct(product, rebate.instantRebate)} instant rebate
              </Typography>
              {rebateUpcoming ? (
                (() => {
                  const caption = em ? rebateWindowCaption(em, formatDate) : null;
                  const startsOn = formatDate(rebate.startDate ?? em?.windowStart);
                  return (
                    <Typography component="span" sx={{ color: 'text.secondary', fontSize: 'inherit' }}>
                      {caption || (startsOn ? `starts ${startsOn}` : 'not started yet')}
                    </Typography>
                  );
                })()
              ) : (
                (() => {
                  const caption = em ? rebateWindowCaption(em, formatDate) : null;
                  // The caption already carries the end date whenever the window
                  // has one ("from Aug 30 to Sep 5" / "ends Sep 5"), so the amber
                  // end-date emphasis is only for windows it can't state.
                  const captionHasEnd = !!caption && !!em?.windowEnd && !!formatDate(em.windowEnd);
                  return (
                    <>
                      {caption && (
                        <Typography component="span" sx={{ color: 'text.secondary', fontSize: 'inherit' }}>{caption}</Typography>
                      )}
                      {!captionHasEnd && rebate.endDate && (
                        <Typography component="span" sx={{ fontWeight: 600, color: tokens.signal.caution.ink, fontSize: 'inherit' }}>
                          {caption ? '· ' : ''}ends {formatDate(rebate.endDate)}
                        </Typography>
                      )}
                    </>
                  );
                })()
              )}
            </Box>
          )}
          {!rebateActive && sale.isSaleActive && sale.savingsLabel && (
            <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: 'text.primary', mt: 0.25 }}>
              {sale.savingsLabel} sale discount
            </Typography>
          )}
        </Box>

        {privateLocked && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: '9px 13px', borderRadius: '10px', border: '1px dashed', borderColor: tokens.line.default, bgcolor: tokens.surface.sunken }}>
            <Lock sx={{ fontSize: 14, color: tokens.ink.faint }} />
            <Typography sx={{ fontSize: '12.5px', color: tokens.ink.bodyAlt, flex: 1 }}>
              {isVendor
                ? `Dealer cost and reimbursement for ${product.brand} are shown to that brand's verified dealers.`
                : `Dealer cost and reimbursement for ${product.brand} unlock once your dealer authorization for this brand is verified.`}
            </Typography>
            {!isVendor && (
              <Button size="small" variant="text" onClick={() => navigate('/app/store-settings')} sx={{ textTransform: 'none', fontSize: 12.5, whiteSpace: 'nowrap' }}>
                Verify brand
              </Button>
            )}
          </Box>
        )}

        {(em?.raw || credit > 0 || cost > 0) && (
          <Box sx={{ display: 'flex', alignItems: 'stretch', border: '1px solid', borderColor: 'divider', borderRadius: '10px', overflow: 'hidden' }}>
            {!!em?.raw && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, p: '8px 14px', borderRight: credit > 0 || cost > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.ink.faint }}>
                  {rebateActive ? 'MAP after rebate' : 'MAP'}
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                  {money(rebateActive ? em!.afterIR : em!.raw)}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: tokens.ink.faint }}>
                  {rebateActive
                    ? `${money(em!.raw)} − ${money(rebate.instantRebate)} IR`
                    : em!.mapAsOf ? `as of ${formatDate(em!.mapAsOf)}` : ''}
                </Typography>
              </Box>
            )}
            {cost > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, p: '8px 14px', borderRight: credit > 0 ? '1px solid' : 'none', borderColor: 'divider', bgcolor: tokens.surface.sunken }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.ink.bodyAlt }}>
                  <Lock sx={{ fontSize: 11 }} />
                  <span>Dealer cost</span>
                </Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                  {formatAmountForProduct(product, cost)}
                </Typography>
                {fromPromaster ? (
                  <Tooltip
                    title={costDetail?.stale
                      ? t('store.promasterDealerCost.staleTooltip', { date: promasterDate })
                      : t('store.promasterDealerCost.tooltip')}
                  >
                    <Typography sx={{ fontSize: '11px', color: costDetail?.stale ? tokens.signal.caution.ink : tokens.ink.faint }}>
                      {costDetail?.stale
                        ? t('store.promasterDealerCost.staleCaption', { date: promasterDate })
                        : t('store.promasterDealerCost.costCaption', { date: promasterDate })}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography sx={{ fontSize: '11px', color: tokens.ink.faint }}>Your cost · never advertised</Typography>
                )}
              </Box>
            )}
            {credit > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, p: '8px 14px', bgcolor: tokens.signal.caution.tint }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.signal.caution.ink }}>
                  <Lock sx={{ fontSize: 11 }} />
                  <span>Dealer reimbursement</span>
                </Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                  {formatAmountForProduct(product, credit)}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: tokens.signal.caution.ink }}>Internal — never advertised</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {missingPricePending && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: '11px 13px', borderRadius: '10px', bgcolor: tokens.signal.caution.tint, border: '1px solid', borderColor: tokens.signal.caution.border }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: tokens.signal.caution.solid }} />
            <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: 'text.primary' }}>No advertised price to sync</Typography>
          </Box>
          <Typography sx={{ fontSize: '12.5px', lineHeight: 1.55, color: 'text.secondary' }}>
            {isPlatformAdmin
              ? 'ERP sync skipped this SKU because retail, MAP, and MSRP are all missing or $0. Add manufacturer pricing so stores can list it.'
              : 'ERP sync skipped this SKU because Motzu has no price to push. Motzu admins have been flagged. Set a store price override if you still want it listed.'}
          </Typography>
        </Box>
      )}

      {review && review.status !== 'rejected' && (
        <SuspiciousDiscountNote
          review={review}
          money={money}
          isPlatformAdmin={isPlatformAdmin}
          onReviewDiscount={onReviewDiscount}
          reviewPending={reviewPending}
        />
      )}

      {rows.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1.5, borderTop: '1px solid', borderColor: tokens.surface.tint }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>
              Price change history
            </Typography>
            {(vendorUpdatedAt || lastCheckedAt) && (
              <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>
                {vendorUpdatedAt ? `Last change ${formatDate(vendorUpdatedAt)}` : ''}
                {vendorUpdatedAt && lastCheckedAt ? ' · ' : ''}
                {lastCheckedAt ? `checked ${formatDate(lastCheckedAt)}` : ''}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {shownRows.map((row) => (
              <Box
                key={row.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '168px 1fr' },
                  gap: { xs: 0.4, sm: 1.5 },
                  alignItems: 'baseline',
                  py: 0.75,
                  borderBottom: '1px solid',
                  borderColor: tokens.surface.sunken,
                  fontSize: '12.5px',
                }}
              >
                <Typography sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', fontSize: 'inherit', minWidth: 0 }}>
                  {formatDate(row.changedAt || row.newValue?.effectiveDate) || '—'}
                </Typography>
                <Typography sx={{ color: 'text.primary', fontVariantNumeric: 'tabular-nums', fontSize: 'inherit', minWidth: 0 }}>
                  {historyPrimaryLine(row, (n) => formatMoneyForProduct(product, n))}
                </Typography>
              </Box>
            ))}
          </Box>
          {rows.length > 3 && (
            <Button
              size="small"
              onClick={() => setHistoryOpen((prev) => !prev)}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600, fontSize: '12.5px', color: 'primary.main', p: 0, minWidth: 0 }}
            >
              {historyOpen ? 'Show less' : `Show all ${rows.length} changes`}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

function SuspiciousDiscountNote({
  review,
  money,
  isPlatformAdmin,
  onReviewDiscount,
  reviewPending,
}: {
  review: NonNullable<NonNullable<CameraWithPricing['pricing']>['suspiciousDiscountReview']>;
  /** Product-aware money formatter from the parent block. */
  money: (n: number | null | undefined) => string;
  isPlatformAdmin: boolean;
  onReviewDiscount: (decision: 'approved' | 'rejected') => void;
  reviewPending: boolean;
}) {
  const advertised = Number(review.advertisedPrice);
  const floor = Number(review.floor);
  const statusLabel = review.status === 'approved' ? 'Approved for sync' : 'Held for review';
  const dotColor = review.status === 'approved' ? tokens.signal.positive.solid : tokens.signal.caution.solid;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: '11px 13px', borderRadius: '10px', bgcolor: tokens.surface.sunken, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dotColor }} />
        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: 'text.primary' }}>{statusLabel}</Typography>
      </Box>
      <Typography sx={{ fontSize: '12.5px', lineHeight: 1.55, color: 'text.secondary' }}>
        Advertised {money(advertised)} is under 50% of stored {review.floorKind === 'map' ? 'MAP' : 'MSRP'} {money(floor)}.
        {review.status === 'pending'
          ? isPlatformAdmin
            ? ' Check the stored catalog and market pricing before approving a store sync.'
            : ' Motzu admins will review the stored catalog pricing before this price is pushed.'
          : ' Future pricing syncs will push this price.'}
      </Typography>
      {isPlatformAdmin && review.status === 'pending' && (
        <Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
          <Button
            size="small"
            variant="contained"
            disabled={reviewPending}
            onClick={() => onReviewDiscount('approved')}
            sx={{ textTransform: 'none', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            Approve sync
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={reviewPending}
            onClick={() => onReviewDiscount('rejected')}
            sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
          >
            Reject
          </Button>
        </Stack>
      )}
    </Box>
  );
}
